import React, { useEffect, useState, useRef, useContext } from 'react';
import { Header, Footer, Dialog, LogoutForm } from 'components';
import { LocalContext } from 'context';
import { navigate } from 'gatsby';
import { createGlobalStyle } from 'styled-components';
import { FirebaseContext, useAuth } from 'firebase';
import { useLocation } from '@reach/router';
import '../../assets/fonts/fonts.css';

const GlobalStyles = createGlobalStyle`
  :root {
    --purple: #cc5eae;
    --blue: #007bff;
    --light-blue: #d3e3fd;
    --dark-blue: #0A2768;
    --red: #fb5a5d;
    --brown: #434343;
    --light-brown: #808080;
    --light-grey: #e2e4ea;
    --off-white: #f2f4ff;
    --bg-color: #F5F5F5;
    --border-radius-lg: 50px;
    --border-radius-md: 10px;
    --border-radius-sm: 4px;
  }

  * {
    margin:0;
    padding:0;
    box-sizing:border-box;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    box-sizing: border-box;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  button {
    color: var(--brown);
    font-family: 'Poppins', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    background-color: var(--bg-color);
    color: var(--brown);
    max-width: 191rem; /* 3056px (i.e., the max width of the mountains bg image) */
    margin: 0 auto;
    overflow-x: hidden;
  }

  body,
  input,
  input::placeholder,
  table {
    font-family: 'Inter', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  input {
    border-radius: var(--border-radius-sm);
    border: 0.063em solid var(--light-grey);
    font-size: 1rem;
    padding: 1.15em 1.15em;

    &::placeholder {
      color: var(--light-brown);
    }

    &:focus {
      background: rgba(226, 228, 234, 0.40);
      color: var(--brown);
      outline: 1px solid var(--blue);
    }
  }
`;

function Layout({ children }) {
  const { user, firebase, loading } = useAuth();
  const { handleDialog } = useContext(LocalContext);
  const { pathname } = useLocation();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const logoutDialogRef = useRef(null);

  // Fixes a problem with bg image not loading in properly on Session page
  useEffect(() => {
    const triggerResize = () => {
      window.dispatchEvent(new Event('resize'));
    };
    setTimeout(() => triggerResize(), 100);
  }, [pathname]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await firebase.userProfile.logout();
      navigate('/');
      handleDialog({
        dialogRef: logoutDialogRef,
        animation: 'close',
        stateHandler: () => setShowLogoutDialog(false),
        callback: () => setLoggingOut(false)
      });
    } catch (error) {
      console.error('Logout error:', error);
      setLoggingOut(false);
    }
  };

  const handleDismissLogoutDialog = (e) => {
    e.stopPropagation();
    handleDialog({
      dialogRef: logoutDialogRef,
      animation: 'dismiss',
      stateHandler: () => setShowLogoutDialog(false)
    });
  };

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <FirebaseContext.Provider value={{ user, firebase, loading }}>
      <GlobalStyles />
      {user && pathname !== '/' && !pathname.includes('/analytics') && (
        <Header setShowLogoutDialog={setShowLogoutDialog} />
      )}
      {children}
      {(pathname === '/' || (pathname !== '/' && user)) && <Footer />}
      <Dialog
        ref={logoutDialogRef}
        isVisible={showLogoutDialog}
        onDismiss={handleDismissLogoutDialog}
        contentStyle={{ maxWidth: '454px' }}>
        <LogoutForm
          onConfirm={handleLogout}
          onDismiss={handleDismissLogoutDialog}
          loggingOut={loggingOut}
        />
      </Dialog>
    </FirebaseContext.Provider>
  );
}

export default Layout;
